import { useCallback, useRef, useState } from "react";

// HR-11, PG-57, #389: a choice saved on the profile, applied at once. The
// optimistic copy (`draft`) lives until every queued write settles; the
// writes run one after another so two quick edits cannot land out of order.
// No `save`: the draft holds for the visit only. Shared by the widget layout
// (useWidgetLayout) and the exercise tracker (useExerciseTracker).
export function useOptimisticSave<V, S>(save: ((next: S) => Promise<unknown>) | undefined) {
  const [draft, setDraft] = useState<V | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const saveRef = useRef(save);
  saveRef.current = save;
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const pending = useRef(0);

  /** Shows `next` at once and queues the write of `toStore`. */
  const commit = useCallback((next: V, toStore: S) => {
    setDraft(next);
    setSaveFailed(false);
    if (!saveRef.current) return;
    pending.current += 1;
    chain.current = chain.current
      // Read at run time: a host that swapped its seam meanwhile gets the write.
      .then(() => saveRef.current?.(toStore))
      .catch(() => setSaveFailed(true))
      .finally(() => {
        pending.current -= 1;
        if (pending.current === 0) setDraft(null);
      });
  }, []);

  return { draft, saveFailed, commit };
}
